import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import {
  UploadSimple, FileXls, FilePdf, ShieldCheck, Users,
  MagnifyingGlass, CheckCircle, PencilSimple, X, Gear, ArrowsClockwise, Star,
} from "@phosphor-icons/react";

const STATUS_CLS = {
  "Pending Review": "bg-slate-100 text-slate-600 border-slate-300",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
  Modify: "bg-amber-50 text-amber-700 border-amber-300",
  Remove: "bg-red-50 text-red-700 border-red-300",
};

const Stat = ({ label, value, tid, tone }) => (
  <div data-testid={tid} className={`border px-3 py-2 ${tone || "border-border bg-white"}`}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    <div className="text-2xl font-bold leading-tight">{value}</div>
  </div>
);

export default function Cip004R43Review() {
  const [overview, setOverview] = useState(null);
  const [entries, setEntries] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [fPriv, setFPriv] = useState(false);
  const [fUnassigned, setFUnassigned] = useState(false);
  const [fStatus, setFStatus] = useState("");
  const [fManager, setFManager] = useState("");
  const [fType, setFType] = useState("");
  const [selected, setSelected] = useState({});
  const [assignName, setAssignName] = useState("");
  const [reviewFor, setReviewFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [keywords, setKeywords] = useState("");
  const adRef = useRef(null);
  const gmRef = useRef(null);
  const mapRef = useRef(null);

  const loadOverview = useCallback(async () => {
    try { const r = await api.get("/cip004/r43/overview"); setOverview(r.data); }
    catch (e) { /* first load may be empty */ }
  }, []);
  const loadManagers = useCallback(async () => {
    try { const r = await api.get("/cip004/r43/managers"); setManagers(r.data.managers || []); }
    catch (e) {}
  }, []);
  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.set("search", search);
      if (fPriv) p.set("privileged", "true");
      if (fUnassigned) p.set("unassigned", "true");
      if (fStatus) p.set("review_status", fStatus);
      if (fManager) p.set("role_owner", fManager);
      if (fType) p.set("identity_type", fType);
      const r = await api.get(`/cip004/r43/entries?${p}`);
      setEntries(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load matrix"); }
    finally { setLoading(false); }
  }, [search, fPriv, fUnassigned, fStatus, fManager, fType]);

  useEffect(() => { loadOverview(); loadManagers(); }, [loadOverview, loadManagers]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const refreshAll = () => { loadOverview(); loadManagers(); loadEntries(); };

  const runImport = async () => {
    const ad = adRef.current?.files?.[0];
    const gm = gmRef.current?.files?.[0];
    if (!ad && !gm) { toast.error("Choose the AD accounts and/or group membership export first."); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      if (ad) fd.append("ad_accounts", ad);
      if (gm) fd.append("group_memberships", gm);
      const r = await api.post("/cip004/r43/import", fd);
      toast.success(`Imported: ${r.data.created} new, ${r.data.updated} updated (${r.data.total} identities, ${r.data.privileged} privileged)`);
      if (adRef.current) adRef.current.value = "";
      if (gmRef.current) gmRef.current.value = "";
      refreshAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
    finally { setImporting(false); }
  };

  const uploadMap = async () => {
    const f = mapRef.current?.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/cip004/r43/manager-map/upload", fd);
      toast.success(`Manager mapping applied to ${r.data.applied} identities (${r.data.unmatched} unmatched)`);
      if (mapRef.current) mapRef.current.value = "";
      refreshAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleAll = (v) => {
    const next = {};
    if (v) entries.forEach((e) => { next[e.id] = true; });
    setSelected(next);
  };

  const bulkAssign = async () => {
    if (!assignName.trim()) { toast.error("Enter a Role Owner (manager) name."); return; }
    if (!selectedIds.length) { toast.error("Select identities to assign."); return; }
    try {
      const r = await api.post("/cip004/r43/entries/assign-manager", { entry_ids: selectedIds, role_owner: assignName.trim() });
      toast.success(`Assigned ${r.data.updated} identities to ${r.data.role_owner}`);
      setSelected({}); setAssignName("");
      refreshAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Assign failed"); }
  };

  const download = async (url, fallbackName) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : fallbackName;
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };

  const submitReview = async (outcome) => {
    try {
      await api.post("/cip004/r43/review", {
        entry_id: reviewFor.id, outcome,
        notes: reviewFor._notes || "", attestation_name: reviewFor._sign || "",
      });
      toast.success(`Marked ${outcome}`);
      setReviewFor(null);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Review failed"); }
  };

  const saveEdit = async () => {
    try {
      const body = {
        role_owner: editFor.role_owner || "", employment_type: editFor.employment_type || "",
        department: editFor.department || "", job_title: editFor.job_title || "",
        privileged_flag: !!editFor.privileged_flag, ticket_ref: editFor.ticket_ref || "",
        shared_account_owner: editFor.shared_account_owner || "", authorized_users: editFor.authorized_users || "",
      };
      await api.patch(`/cip004/r43/entries/${editFor.id}`, body);
      toast.success("Saved");
      setEditFor(null);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  const openSettings = async () => {
    try { const r = await api.get("/cip004/r43/settings"); setKeywords((r.data.privileged_keywords || []).join(", ")); setShowSettings(true); }
    catch (e) { toast.error("Failed to load settings"); }
  };
  const saveSettings = async () => {
    try {
      await api.put("/cip004/r43/settings", { privileged_keywords: keywords.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Privileged keywords saved — re-import to re-flag");
      setShowSettings(false);
    } catch (e) { toast.error("Save failed"); }
  };

  const inputCls = "border border-input bg-background px-2 py-1.5 text-sm w-full rounded-none";

  return (
    <div data-testid="r43-panel">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><ShieldCheck size={20} weight="duotone" /> R4.3 — Consolidated User-Centric Matrix</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">Import your AD account &amp; group-membership exports to auto-build a user-centric access matrix, assign Role Owners, run semiannual manager attestations, and export per-manager review packets (feeds R4.2 quarterly reviews).</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={openSettings} data-testid="r43-settings-btn"><Gear size={14} /> Privileged rules</Button>
          <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => download("/cip004/r43/export/workbook.xlsx", "CUCM.xlsx")} data-testid="r43-export-workbook"><FileXls size={14} /> Full workbook</Button>
        </div>
      </div>

      {/* Import bar */}
      <div className="border border-border bg-secondary/40 p-3 mb-4" data-testid="r43-import-bar">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">AD accounts export (.xlsx)</label>
            <input ref={adRef} type="file" accept=".xlsx" className="block text-xs mt-1" data-testid="r43-ad-file" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Group membership export (.xlsx)</label>
            <input ref={gmRef} type="file" accept=".xlsx" className="block text-xs mt-1" data-testid="r43-gm-file" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-none gap-1" onClick={runImport} disabled={importing} data-testid="r43-import-btn">
              <UploadSimple size={14} /> {importing ? "Importing…" : "Build / refresh matrix"}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          <span>Manager mapping (username → manager):</span>
          <input ref={mapRef} type="file" accept=".xlsx" className="text-xs" data-testid="r43-map-file" onChange={uploadMap} />
          <span className="text-muted-foreground/70">Existing manager assignments &amp; review decisions are preserved on re-import.</span>
        </div>
      </div>

      {/* Overview */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4" data-testid="r43-overview">
          <Stat label="Identities" value={overview.total} tid="r43-stat-total" />
          <Stat label="Privileged" value={overview.privileged} tid="r43-stat-priv" tone="border-red-200 bg-red-50/60" />
          <Stat label="No manager" value={overview.unassigned_manager} tid="r43-stat-unassigned" tone={overview.unassigned_manager ? "border-amber-200 bg-amber-50/60" : "border-border bg-white"} />
          <Stat label="No AD record" value={overview.no_ad_record} tid="r43-stat-noad" />
          <Stat label="Reviewed" value={overview.reviewed} tid="r43-stat-reviewed" tone="border-emerald-200 bg-emerald-50/60" />
          <Stat label="Role Owners" value={(overview.managers || []).length} tid="r43-stat-managers" />
        </div>
      )}

      {/* Managers panel */}
      {managers.length > 0 && (
        <div className="border border-border mb-4" data-testid="r43-managers">
          <div className="bg-secondary px-3 py-2 font-semibold text-sm flex items-center gap-2"><Users size={16} /> Per-manager review packets</div>
          <div className="overflow-x-auto">
            <table className="text-sm min-w-full">
              <thead className="bg-secondary/40"><tr>
                <th className="px-3 py-1.5 text-left font-medium">Role Owner</th>
                <th className="px-3 py-1.5 text-left font-medium">People</th>
                <th className="px-3 py-1.5 text-left font-medium">Privileged</th>
                <th className="px-3 py-1.5 text-left font-medium">Reviewed</th>
                <th className="px-3 py-1.5 text-left font-medium">Export</th>
              </tr></thead>
              <tbody>
                {managers.map((m) => (
                  <tr key={m.role_owner} className="border-t border-border" data-testid={`r43-mgr-${m.role_owner}`}>
                    <td className="px-3 py-1.5">{m.role_owner === "(Unassigned)" ? <span className="text-amber-700">{m.role_owner}</span> : m.role_owner}</td>
                    <td className="px-3 py-1.5">{m.people}</td>
                    <td className="px-3 py-1.5">{m.privileged}</td>
                    <td className="px-3 py-1.5">{m.reviewed}/{m.people}</td>
                    <td className="px-3 py-1.5">
                      {m.role_owner !== "(Unassigned)" && (
                        <span className="flex gap-2">
                          <button className="text-emerald-700 hover:underline inline-flex items-center gap-1" onClick={() => download(`/cip004/r43/export/manager.xlsx?role_owner=${encodeURIComponent(m.role_owner)}`, "CUCM.xlsx")} data-testid={`r43-mgr-xlsx-${m.role_owner}`}><FileXls size={13} /> .xlsx</button>
                          <button className="text-red-700 hover:underline inline-flex items-center gap-1" onClick={() => download(`/cip004/r43/export/manager.pdf?role_owner=${encodeURIComponent(m.role_owner)}`, "Attestation.pdf")} data-testid={`r43-mgr-pdf-${m.role_owner}`}><FilePdf size={13} /> sign PDF</button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters + bulk assign */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
          <input className={`${inputCls} pl-7 w-56`} placeholder="Search user / group / manager" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="r43-search" />
        </div>
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={fPriv} onChange={(e) => setFPriv(e.target.checked)} data-testid="r43-filter-priv" /> Privileged only</label>
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={fUnassigned} onChange={(e) => setFUnassigned(e.target.checked)} data-testid="r43-filter-unassigned" /> Unassigned manager</label>
        <select className="border border-input bg-background px-2 py-1 text-xs rounded-none" value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="r43-filter-status">
          <option value="">All statuses</option>
          <option>Pending Review</option><option>Approved</option><option>Modify</option><option>Remove</option>
        </select>
        <select className="border border-input bg-background px-2 py-1 text-xs rounded-none" value={fType} onChange={(e) => setFType(e.target.value)} data-testid="r43-filter-type">
          <option value="">All identity types</option>
          <option>User (AD account)</option>
          <option>User (no AD record)</option>
          <option>Computer (machine account)</option>
          <option>Group (nested)</option>
        </select>
        <select className="border border-input bg-background px-2 py-1 text-xs rounded-none" value={fManager} onChange={(e) => setFManager(e.target.value)} data-testid="r43-filter-manager">
          <option value="">All managers</option>
          {(overview?.managers || []).map((m) => <option key={m}>{m}</option>)}
        </select>
        <button className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={refreshAll} data-testid="r43-refresh"><ArrowsClockwise size={13} /> Refresh</button>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-2 mb-2 bg-blue-50 border border-blue-200 px-3 py-2 text-sm" data-testid="r43-bulk-bar">
          <span className="font-semibold">{selectedIds.length} selected</span>
          <input className={`${inputCls} w-56`} placeholder="Role Owner (manager) name" value={assignName} onChange={(e) => setAssignName(e.target.value)} data-testid="r43-assign-name" />
          <Button size="sm" className="rounded-none gap-1" onClick={bulkAssign} data-testid="r43-assign-btn"><Users size={14} /> Assign manager</Button>
          <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setSelected({})}>Clear</button>
        </div>
      )}

      {/* Matrix table */}
      <div className="border border-border overflow-x-auto" data-testid="r43-table">
        <table className="text-sm min-w-full">
          <thead className="bg-secondary/50"><tr>
            <th className="px-2 py-1.5"><input type="checkbox" onChange={(e) => toggleAll(e.target.checked)} checked={entries.length > 0 && selectedIds.length === entries.length} data-testid="r43-select-all" /></th>
            <th className="px-3 py-1.5 text-left font-medium">Identity</th>
            <th className="px-3 py-1.5 text-left font-medium">Type</th>
            <th className="px-3 py-1.5 text-left font-medium">Status</th>
            <th className="px-3 py-1.5 text-left font-medium">Group / Role Memberships</th>
            <th className="px-3 py-1.5 text-left font-medium">Priv</th>
            <th className="px-3 py-1.5 text-left font-medium">Role Owner</th>
            <th className="px-3 py-1.5 text-left font-medium">Review</th>
            <th className="px-3 py-1.5 text-left font-medium">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground" data-testid="r43-empty">No identities yet. Import your AD account &amp; group-membership exports above.</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id} className="border-t border-border align-top hover:bg-secondary/20" data-testid={`r43-row-${e.id}`}>
                <td className="px-2 py-1.5"><input type="checkbox" checked={!!selected[e.id]} onChange={(ev) => setSelected((s) => ({ ...s, [e.id]: ev.target.checked }))} data-testid={`r43-select-${e.id}`} /></td>
                <td className="px-3 py-1.5 font-medium whitespace-nowrap">{e.unique_identifier}{!e.has_ad_record && <span className="ml-1 text-[10px] text-amber-600" title="No AD account record">⚠</span>}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">{e.identity_type}</td>
                <td className="px-3 py-1.5 text-xs whitespace-nowrap">{e.account_status}</td>
                <td className="px-3 py-1.5 text-xs max-w-md"><span className="line-clamp-2" title={e.memberships_str}>{e.memberships_str || "—"}</span></td>
                <td className="px-3 py-1.5">{e.privileged_flag ? <span className="text-[10px] font-bold uppercase border px-1.5 py-0.5 bg-red-50 text-red-700 border-red-300 inline-flex items-center gap-0.5"><Star size={10} weight="fill" />Yes</span> : <span className="text-xs text-muted-foreground">No</span>}</td>
                <td className="px-3 py-1.5 text-xs whitespace-nowrap">{e.role_owner || <span className="text-amber-600">— unassigned</span>}</td>
                <td className="px-3 py-1.5"><span className={`text-[10px] uppercase font-bold border px-1.5 py-0.5 ${STATUS_CLS[e.review_status] || ""}`} title={e.attestation || ""}>{e.review_status}</span></td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <button className="text-emerald-700 hover:underline text-xs mr-2 inline-flex items-center gap-0.5" onClick={() => setReviewFor({ ...e })} data-testid={`r43-review-${e.id}`}><CheckCircle size={13} /> Review</button>
                  <button className="text-muted-foreground hover:text-foreground" onClick={() => setEditFor({ ...e })} data-testid={`r43-edit-${e.id}`}><PencilSimple size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground mt-1">{entries.length} shown</div>

      {/* Review modal */}
      {reviewFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setReviewFor(null)}>
          <div className="bg-background border border-border w-full max-w-lg p-4" onClick={(ev) => ev.stopPropagation()} data-testid="r43-review-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold">Access review — {reviewFor.unique_identifier}</h3>
              <button onClick={() => setReviewFor(null)}><X size={18} /></button>
            </div>
            <div className="text-xs text-muted-foreground mb-3 space-y-0.5">
              <div><b>Role Owner:</b> {reviewFor.role_owner || "—"}</div>
              <div><b>Privileged:</b> {reviewFor.privileged_flag ? "Yes" : "No"} · <b>Status:</b> {reviewFor.account_status}</div>
              <div className="max-h-24 overflow-auto"><b>Memberships:</b> {reviewFor.memberships_str || "—"}</div>
            </div>
            <label className="text-xs font-semibold">Notes / justification</label>
            <textarea className={`${inputCls} h-20 mb-2`} value={reviewFor._notes || ""} onChange={(ev) => setReviewFor({ ...reviewFor, _notes: ev.target.value })} data-testid="r43-review-notes" />
            <label className="text-xs font-semibold">Attestation signer (name)</label>
            <input className={`${inputCls} mb-3`} placeholder="Manager name" value={reviewFor._sign || ""} onChange={(ev) => setReviewFor({ ...reviewFor, _sign: ev.target.value })} data-testid="r43-review-sign" />
            <div className="flex justify-end gap-2">
              <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700" onClick={() => submitReview("APPROVED")} data-testid="r43-review-approve">Approve</Button>
              <Button size="sm" variant="outline" className="rounded-none text-amber-700 border-amber-300" onClick={() => submitReview("MODIFY")} data-testid="r43-review-modify">Modify</Button>
              <Button size="sm" variant="outline" className="rounded-none text-red-700 border-red-300" onClick={() => submitReview("REMOVE")} data-testid="r43-review-remove">Remove</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditFor(null)}>
          <div className="bg-background border border-border w-full max-w-lg p-4" onClick={(ev) => ev.stopPropagation()} data-testid="r43-edit-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">Edit — {editFor.unique_identifier}</h3>
              <button onClick={() => setEditFor(null)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs">Role Owner (manager)<input className={inputCls} value={editFor.role_owner || ""} onChange={(ev) => setEditFor({ ...editFor, role_owner: ev.target.value })} data-testid="r43-edit-manager" /></label>
              <label className="text-xs">Employment Type<input className={inputCls} value={editFor.employment_type || ""} onChange={(ev) => setEditFor({ ...editFor, employment_type: ev.target.value })} /></label>
              <label className="text-xs">Department<input className={inputCls} value={editFor.department || ""} onChange={(ev) => setEditFor({ ...editFor, department: ev.target.value })} /></label>
              <label className="text-xs">Job Title<input className={inputCls} value={editFor.job_title || ""} onChange={(ev) => setEditFor({ ...editFor, job_title: ev.target.value })} /></label>
              <label className="text-xs">Ticket Ref #<input className={inputCls} value={editFor.ticket_ref || ""} onChange={(ev) => setEditFor({ ...editFor, ticket_ref: ev.target.value })} /></label>
              <label className="text-xs flex items-center gap-2 mt-4"><input type="checkbox" checked={!!editFor.privileged_flag} onChange={(ev) => setEditFor({ ...editFor, privileged_flag: ev.target.checked })} /> Privileged access</label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditFor(null)}>Cancel</Button>
              <Button size="sm" className="rounded-none" onClick={saveEdit} data-testid="r43-edit-save">Save</Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowSettings(false)}>
          <div className="bg-background border border-border w-full max-w-md p-4" onClick={(ev) => ev.stopPropagation()} data-testid="r43-settings-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold">Privileged-group keywords</h3>
              <button onClick={() => setShowSettings(false)}><X size={18} /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-2">A group is auto-flagged as privileged when its name contains any of these (comma-separated, case-insensitive). Re-import to re-apply.</p>
            <textarea className={`${inputCls} h-20`} value={keywords} onChange={(e) => setKeywords(e.target.value)} data-testid="r43-settings-keywords" />
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button size="sm" className="rounded-none" onClick={saveSettings} data-testid="r43-settings-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
